import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";

// SEO metadata per route (title + description in 9 languages)
// Keep concise: Google truncates titles >60 chars, descriptions >160 chars.
const META = {
  "/": {
    title: {
      en: "Al Quseir — Red Sea, Egypt | History, Diving, Sights & Travel Guide",
      de: "Al Quseir — Rotes Meer, Ägypten | Geschichte, Tauchen & Reiseführer",
      fr: "Al Quseir — Mer Rouge, Égypte | Histoire, plongée & guide voyage",
      it: "Al Quseir — Mar Rosso, Egitto | Storia, immersioni e guida viaggio",
      ru: "Эль-Кусейр — Красное море, Египет | История, дайвинг, путеводитель",
      ar: "القصير — البحر الأحمر، مصر | التاريخ والغوص ودليل السفر",
      zh: "库赛尔 — 埃及红海 | 历史、潜水、景点和旅行指南",
      ja: "アル・クセイル — エジプト紅海 | 歴史、ダイビング、観光ガイド",
      da: "Al Quseir — Det Røde Hav, Egypten | Historie, dykning & rejseguide",
    },
    desc: {
      en: "Discover Al Quseir on Egypt's Red Sea: 3,500 years of maritime history, Ottoman fortress, Wadi Hammamat, Mons Claudianus, pristine coral reefs and hotels.",
      de: "Entdecke Al Quseir am Roten Meer: 3.500 Jahre Seegeschichte, osmanische Festung, Wadi Hammamat, Mons Claudianus, unberührte Korallenriffe und Hotels.",
      fr: "Découvrez Al Quseir sur la mer Rouge : 3 500 ans d'histoire maritime, forteresse ottomane, Wadi Hammamat, Mons Claudianus, récifs et hôtels.",
      it: "Scopri Al Quseir sul Mar Rosso: 3.500 anni di storia marittima, fortezza ottomana, Wadi Hammamat, Mons Claudianus, barriere coralline e hotel.",
      ru: "Эль-Кусейр на Красном море: 3500 лет морской истории, османская крепость, Вади-Хаммамат, Монс-Клавдиан, коралловые рифы и отели.",
      ar: "اكتشف القصير على البحر الأحمر: ٣٥٠٠ عام من التاريخ البحري، القلعة العثمانية، وادي حمامات، جبل كلوديانوس، شعاب مرجانية وفنادق.",
      zh: "探索埃及红海库赛尔:3500年海洋历史、奥斯曼堡垒、瓦迪哈玛马特、克劳狄亚山、原始珊瑚礁和酒店。",
      ja: "エジプト紅海のアル・クセイル:3500年の海洋史、オスマン要塞、ワディ・ハンママット、モンス・クラウディアヌス、珊瑚礁、ホテル。",
      da: "Opdag Al Quseir ved Det Røde Hav: 3.500 års maritim historie, osmannisk fæstning, Wadi Hammamat, Mons Claudianus, koralrev og hoteller.",
    },
  },
  "/history": {
    title: {
      en: "History of Al Quseir | 3,500 Years of Maritime Heritage",
      de: "Geschichte von Al Quseir | 3.500 Jahre Seegeschichte",
      fr: "Histoire d'Al Quseir | 3 500 ans de patrimoine maritime",
      it: "Storia di Al Quseir | 3.500 anni di storia marittima",
      ru: "История Эль-Кусейра | 3500 лет морского наследия",
      ar: "تاريخ القصير | ٣٥٠٠ عام من التراث البحري",
      zh: "库赛尔历史 | 3500年海洋文明",
      ja: "アル・クセイルの歴史 | 3500年の海洋遺産",
      da: "Al Quseirs historie | 3.500 års maritim arv",
    },
    desc: {
      en: "From Pharaonic Punt expeditions and Roman Myos Hormos to Ottoman fortress and British phosphate era — 3,500 years of Al Quseir's maritime story.",
      de: "Von pharaonischen Punt-Expeditionen und dem römischen Myos Hormos über die osmanische Festung bis zur britischen Phosphat-Ära.",
      fr: "Des expéditions pharaoniques à Pount et Myos Hormos romain à la forteresse ottomane et l'ère du phosphate britannique.",
      it: "Dalle spedizioni faraoniche a Punt e Myos Hormos romano alla fortezza ottomana e l'era britannica del fosfato.",
      ru: "От фараоновых экспедиций в Пунт и римского порта Миос-Хормос до османской крепости и британской эры фосфатов.",
      ar: "من بعثات بلاد بونت الفرعونية وميناء ميوس هورموس الروماني إلى القلعة العثمانية وحقبة الفوسفات البريطانية.",
      zh: "从法老时期蓬特远征、罗马米奥斯霍尔莫斯港到奥斯曼堡垒和英国磷矿时代。",
      ja: "ファラオのプント遠征、ローマのミオス・ホルモス港から、オスマン要塞、英国リン鉱石時代まで。",
      da: "Fra faraoniske Punt-ekspeditioner og romersk Myos Hormos til osmannisk fæstning og den britiske fosfatæra.",
    },
  },
  "/sights": {
    title: {
      en: "Sights of Al Quseir | Ottoman Fortress, Al-Farran Mosque & Old Town",
      de: "Sehenswürdigkeiten Al Quseir | Osmanische Festung, Al-Farran-Moschee",
      fr: "Sites d'Al Quseir | Forteresse ottomane, mosquée Al-Farran",
      it: "Luoghi di Al Quseir | Fortezza ottomana, moschea Al-Farran",
      ru: "Достопримечательности Эль-Кусейра | Османская крепость, мечеть Аль-Фарран",
      ar: "معالم القصير | القلعة العثمانية، مسجد الفران، البلدة القديمة",
      zh: "库赛尔景点 | 奥斯曼堡垒、阿尔法兰清真寺、老城区",
      ja: "アル・クセイルの観光名所 | オスマン要塞、アル=ファッラン・モスク、旧市街",
      da: "Al Quseirs seværdigheder | Osmannisk fæstning, Al-Farran-moské",
    },
    desc: {
      en: "Explore Al Quseir's landmarks: 1571 Ottoman fortress, historic Al-Farran Mosque, coral-stone old town, British phosphate buildings and Wadi Hammamat.",
      de: "Osmanische Festung (1571), Al-Farran-Moschee, Korallensteinhäuser, britische Phosphatgebäude und Wadi Hammamat — entdecke die Highlights.",
      fr: "Forteresse ottomane de 1571, mosquée Al-Farran, maisons en pierre de corail, bâtiments des phosphates britanniques et Wadi Hammamat.",
      it: "Fortezza ottomana del 1571, moschea Al-Farran, case di pietra corallina, edifici dei fosfati britannici e Wadi Hammamat.",
      ru: "Османская крепость 1571 года, мечеть Аль-Фарран, коралловые дома, британские фосфатные здания и Вади-Хаммамат.",
      ar: "قلعة عثمانية من عام ١٥٧١، مسجد الفران، بيوت الحجر المرجاني، مباني الفوسفات البريطانية، ونقوش وادي حمامات.",
      zh: "1571年奥斯曼堡垒、阿尔法兰清真寺、珊瑚石老房子、英国磷矿建筑和瓦迪哈玛马特岩画。",
      ja: "1571年オスマン要塞、アル=ファッラン・モスク、珊瑚石の旧市街、英国リン鉱石建築、ワディ・ハンママット。",
      da: "Osmannisk fæstning fra 1571, Al-Farran-moské, koralstenshuse, britiske fosfatbygninger og Wadi Hammamat.",
    },
  },
  "/beaches": {
    title: {
      en: "Beaches & Diving in Al Quseir | Red Sea Coral Reefs",
      de: "Strände & Tauchen in Al Quseir | Korallenriffe am Roten Meer",
      fr: "Plages & plongée Al Quseir | Récifs coralliens de la mer Rouge",
      it: "Spiagge & immersioni Al Quseir | Barriere coralline Mar Rosso",
      ru: "Пляжи и дайвинг Эль-Кусейра | Коралловые рифы Красного моря",
      ar: "شواطئ القصير والغوص | شعاب البحر الأحمر المرجانية",
      zh: "库赛尔海滩与潜水 | 红海珊瑚礁",
      ja: "アル・クセイルのビーチとダイビング | 紅海の珊瑚礁",
      da: "Strande og dykning Al Quseir | Det Røde Havs koralrev",
    },
    desc: {
      en: "Al Quseir offers some of the Red Sea's most pristine house reefs, accessible directly from shore. Napoleon fish, turtles, dolphins — beyond mass tourism.",
      de: "Al Quseir bietet einige der unberührtesten Hausriffe des Roten Meeres, direkt vom Strand erreichbar. Napoleonfische, Schildkröten, Delfine.",
      fr: "Al Quseir offre certains des récifs maison les plus préservés de la mer Rouge, accessibles directement depuis le rivage. Poissons-napoléon, tortues.",
      it: "Al Quseir offre alcune delle barriere più incontaminate del Mar Rosso, accessibili direttamente dalla spiaggia. Pesci napoleone, tartarughe.",
      ru: "Эль-Кусейр — одни из самых нетронутых домашних рифов Красного моря, доступных прямо с берега. Рыбы-наполеоны, черепахи, дельфины.",
      ar: "تقدم القصير بعضاً من أنقى الشعاب المرجانية في البحر الأحمر، يمكن الوصول إليها مباشرة من الشاطئ. أسماك نابليون، سلاحف، دلافين.",
      zh: "库赛尔拥有红海最原始的家庭珊瑚礁,可直接从海岸到达。拿破仑鱼、海龟、海豚。",
      ja: "アル・クセイルは、海岸から直接アクセスできる紅海で最も手つかずのハウスリーフを誇ります。ナポレオンフィッシュ、ウミガメ、イルカ。",
      da: "Al Quseir byder på nogle af Det Røde Havs mest uberørte house reefs, tilgængelige direkte fra kysten. Napoleonfisk, skildpadder, delfiner.",
    },
  },
  "/mountains": {
    title: {
      en: "Mountains & Desert | Wadi Hammamat, Mons Claudianus & Mons Porphyrites",
      de: "Berge & Wüste | Wadi Hammamat, Mons Claudianus & Mons Porphyrites",
      fr: "Montagnes & désert | Wadi Hammamat, Mons Claudianus, Mons Porphyrites",
      it: "Montagne e deserto | Wadi Hammamat, Mons Claudianus, Mons Porphyrites",
      ru: "Горы и пустыня | Вади-Хаммамат, Монс-Клавдиан, Монс-Порфирит",
      ar: "الجبال والصحراء | وادي حمامات وجبل كلوديانوس وجبل بورفيريتس",
      zh: "山脉与沙漠 | 瓦迪哈玛马特、克劳狄亚山、波菲利特斯山",
      ja: "山と砂漠 | ワディ・ハンママット、モンス・クラウディアヌス、モンス・ポルフィリテス",
      da: "Bjerge & ørken | Wadi Hammamat, Mons Claudianus, Mons Porphyrites",
    },
    desc: {
      en: "The Red Sea Hills behind Al Quseir hold ancient Roman quarries, pharaonic petroglyphs, Bedouin trails and emerald mines — three millennia of desert history.",
      de: "Die Red Sea Hills bergen antike römische Steinbrüche, pharaonische Felszeichnungen, Beduinenpfade und Smaragdminen — drei Jahrtausende Wüstengeschichte.",
      fr: "Les collines de la mer Rouge recèlent carrières romaines antiques, pétroglyphes pharaoniques, pistes bédouines et mines d'émeraudes.",
      it: "Le colline del Mar Rosso custodiscono antiche cave romane, petroglifi faraonici, piste beduine e miniere di smeraldi.",
      ru: "В горах Красного моря скрыты древнеримские каменоломни, фараоновые петроглифы, бедуинские тропы и изумрудные шахты.",
      ar: "تحتضن تلال البحر الأحمر محاجر رومانية قديمة، نقوشاً فرعونية، مسارات بدوية، ومناجم زمرد — ثلاثة آلاف سنة من تاريخ الصحراء.",
      zh: "库赛尔背后的红海山脉藏有古罗马采石场、法老岩画、贝都因小径和绿宝石矿——三千年沙漠历史。",
      ja: "アル・クセイル背後の紅海山地には、古代ローマの採石場、ファラオの岩絵、ベドウィンの道、エメラルド鉱山があります。",
      da: "Red Sea Hills bag Al Quseir rummer antikke romerske stenbrud, faraoniske helleristninger, beduinstier og smaragdminer.",
    },
  },
  "/directory": {
    title: {
      en: "Al Quseir Directory | Hotels, Restaurants, Diving Centers & Shops",
      de: "Al Quseir Verzeichnis | Hotels, Restaurants, Tauchzentren & Shops",
      fr: "Annuaire Al Quseir | Hôtels, restaurants, centres de plongée & boutiques",
      it: "Directory Al Quseir | Hotel, ristoranti, centri subacquei & negozi",
      ru: "Справочник Эль-Кусейра | Отели, рестораны, дайвинг-центры, магазины",
      ar: "دليل القصير | فنادق ومطاعم ومراكز غوص ومحلات",
      zh: "库赛尔黄页 | 酒店、餐厅、潜水中心与商店",
      ja: "アル・クセイル ディレクトリ | ホテル、レストラン、ダイビング、ショップ",
      da: "Al Quseir guide | Hoteller, restauranter, dykkercentre & butikker",
    },
    desc: {
      en: "Find vetted hotels, restaurants, diving centers and local shops in Al Quseir. A curated directory for travelers who want the authentic experience.",
      de: "Geprüfte Hotels, Restaurants, Tauchzentren und lokale Shops in Al Quseir. Kuratiertes Verzeichnis für Reisende, die das Echte suchen.",
      fr: "Hôtels, restaurants, centres de plongée et commerces locaux d'Al Quseir. Annuaire sélectionné pour voyageurs en quête d'authenticité.",
      it: "Hotel, ristoranti, centri subacquei e negozi locali ad Al Quseir. Directory selezionata per viaggiatori che cercano autenticità.",
      ru: "Проверенные отели, рестораны, дайвинг-центры и магазины Эль-Кусейра. Подобранный справочник для искателей подлинности.",
      ar: "فنادق ومطاعم ومراكز غوص ومحلات محلية مختارة في القصير لمن يبحث عن التجربة الأصيلة.",
      zh: "库赛尔精选酒店、餐厅、潜水中心和本地商铺——适合追求真实体验的旅行者。",
      ja: "アル・クセイルの厳選ホテル、レストラン、ダイビングセンター、ローカルショップ。本物の体験を求める旅行者へ。",
      da: "Udvalgte hoteller, restauranter, dykkercentre og lokale butikker i Al Quseir — til den autentiske oplevelse.",
    },
  },
};

const BASE_URL = "https://www.alquseir.com";
const LANGS = ["en", "de", "fr", "it", "ru", "ar", "zh", "ja", "da"];

function setMeta(name, content, attr = "name") {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href, hreflang) {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]`;
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hreflang) el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export default function SeoHead() {
  const location = useLocation();
  const { lang } = useI18n();

  useEffect(() => {
    const path = location.pathname === "/" ? "/" : location.pathname.replace(/\/$/, "");
    const meta = META[path] || META["/"];
    const title = meta.title[lang] || meta.title.en;
    const desc = meta.desc[lang] || meta.desc.en;

    // Set <title>
    document.title = title;

    // <html lang=...> and dir
    const html = document.documentElement;
    html.setAttribute("lang", lang);
    html.setAttribute("dir", lang === "ar" ? "rtl" : "ltr");

    // Basic meta
    setMeta("description", desc);
    setMeta("title", title);

    // Canonical
    const search = lang === "en" ? "" : `?lang=${lang}`;
    const canonicalUrl = `${BASE_URL}${path}${search}`;
    setLink("canonical", canonicalUrl);

    // hreflang alternates (clean slate each time)
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
    LANGS.forEach((l) => {
      const s = l === "en" ? "" : `?lang=${l}`;
      setLink("alternate", `${BASE_URL}${path}${s}`, l);
    });
    setLink("alternate", `${BASE_URL}${path}`, "x-default");

    // Open Graph
    setMeta("og:url", canonicalUrl, "property");
    setMeta("og:title", title, "property");
    setMeta("og:description", desc, "property");
    setMeta("og:locale", lang === "en" ? "en_US" : `${lang}_${lang.toUpperCase()}`, "property");

    // Twitter
    setMeta("twitter:url", canonicalUrl);
    setMeta("twitter:title", title);
    setMeta("twitter:description", desc);
  }, [location.pathname, lang]);

  return null;
}
